import type { Currency } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import { CurrencyIcon } from "@loot-os/ui/components/currency-icon";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Field, FieldLabel } from "@loot-os/ui/components/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@loot-os/ui/components/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { cn } from "@loot-os/ui/lib/utils";
import { FunnelIcon, SearchIcon, ZapIcon } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";

import { boxSearchRevealRef } from "../../../lib/boxSearchFocus.js";
import type { AmountField, BoxFiltersContent } from "../../pages/boxes/content/filters.js";

/**
 * The design's filter row from `md`: search, the price range, the sort, "Enough …" and Clear in one
 * line. Below `md` the row leaves the page for a full-screen dialog, opened by a Filters button held
 * above the tab bar.
 */
export function ClassicBoxFilters(filters: BoxFiltersContent) {
  return (
    <>
      <FilterFields {...filters} className="max-md:hidden" />
      <PhoneFilters {...filters} />
    </>
  );
}

/** A phone's filters: a button above the tab bar, and the fields in a full-screen dialog. */
function PhoneFilters(filters: BoxFiltersContent) {
  const [open, setOpen] = useState(false);
  // Opened by the header's search rather than the button: focus the search field, not the popup.
  const [forSearch, setForSearch] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const revealRef = useMemo(
    () =>
      boxSearchRevealRef(() => {
        setForSearch(true);
        setOpen(true);
      }),
    [],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setForSearch(false);
      }}
    >
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.125rem+0.75rem)] z-30 flex justify-center md:hidden">
        <DialogTrigger
          render={
            <Button
              ref={revealRef}
              variant="secondary"
              size="lg"
              className="pointer-events-auto h-11 gap-2 px-5 text-base shadow-lg ring-1 ring-border"
            />
          }
        >
          <FunnelIcon />
          Filters
          {filters.activeCount > 0 && <Badge>{filters.activeCount}</Badge>}
        </DialogTrigger>
      </div>
      <DialogContent
        ref={popupRef}
        // The header's search wants the field; a tap wants the popup, so no keyboard opens.
        initialFocus={(openType) =>
          forSearch
            ? (popupRef.current?.querySelector<HTMLElement>('input[type="search"]') ?? true)
            : openType === "touch"
              ? popupRef.current
              : true
        }
        className="top-0 left-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col overflow-y-auto rounded-none ring-0 sm:max-w-none"
      >
        <DialogTitle className="text-lg font-semibold">Filters</DialogTitle>
        <FilterFields {...filters} />
        <DialogClose render={<Button variant="outline" size="lg" className="mt-auto w-full" />}>
          Done
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}

/** The fields themselves: one line from `md`, stacked below it, the range and the sort each on a line. */
function FilterFields({
  search,
  price,
  sort,
  affordable,
  clear,
  className,
}: BoxFiltersContent & { className?: string }) {
  const affordableId = useId();
  return (
    <div
      className={cn("flex flex-col gap-2.5 md:flex-row md:flex-wrap md:items-center", className)}
    >
      <Field className="md:w-70">
        <InputGroup>
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
          <InputGroupInput
            ref={search.focusRef}
            type="search"
            aria-label={search.label}
            placeholder={search.placeholder}
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
          />
        </InputGroup>
      </Field>
      <div className="flex items-center">
        <PriceInput field={price.min} currency={price.currency} />
        <span aria-hidden className="px-2.5 text-sm font-medium text-foreground">
          -
        </span>
        <PriceInput field={price.max} currency={price.currency} />
      </div>
      <div className="flex items-center gap-2.5">
        <Select
          value={sort.value}
          items={Object.fromEntries(sort.options.map((o) => [o.value, o.label]))}
          onValueChange={(next) => {
            const option = sort.options.find((o) => o.value === next);
            if (option) sort.onValueChange(option.value);
          }}
        >
          <SelectTrigger aria-label={sort.label}>
            <ZapIcon />
            <SelectValue className={cn(sort.value === "default" && "text-muted-foreground")} />
          </SelectTrigger>
          <SelectContent>
            {sort.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {affordable && (
          <Field orientation="horizontal" className="w-auto">
            <Checkbox
              id={affordableId}
              checked={affordable.checked}
              onCheckedChange={(checked) => affordable.onCheckedChange(checked)}
            />
            <FieldLabel htmlFor={affordableId}>{affordable.label}</FieldLabel>
          </Field>
        )}
      </div>
      <Button className="w-full md:ml-auto md:w-auto" onClick={clear.onClear}>
        {clear.label}
      </Button>
    </div>
  );
}

/** One end of the price range: the store's currency icon, then the amount. */
function PriceInput({ field, currency }: { field: AmountField; currency: Currency }) {
  return (
    <InputGroup className="flex-1 md:w-[4.625rem] md:flex-none">
      <InputGroupAddon>
        {/* The icon where the store has one (or the 💎 a symbol-less currency gets); otherwise its
            symbol, since the field holds a bare number. */}
        {currency.iconSvg || currency.symbol === "" ? (
          <CurrencyIcon currency={currency} className="size-4" />
        ) : (
          <InputGroupText>{currency.symbol}</InputGroupText>
        )}
      </InputGroupAddon>
      <InputGroupInput
        inputMode="decimal"
        aria-label={field.label}
        aria-invalid={field.invalid || undefined}
        placeholder={field.placeholder}
        value={field.value}
        onChange={(event) => field.onChange(event.target.value)}
      />
    </InputGroup>
  );
}
